import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useVenueId } from '../hooks/useUserData';
import { fetchTables } from '../utils/tableService';
import Loading from '../components/Loading';
import { fetchFloors } from '../utils/floorService';
import { fetchRoles, fetchUsers } from '../utils/authService';
import { fetchAreas } from '../utils/areaService';
import { fetchMenus } from '../utils/menuService';

const AdminContext = createContext();

const ALLOWED_ITEM_STATUSES = new Set(['draft', 'active', 'archived']);

const normalizeItem = (item, categoryId, menuId) => {
  const rawStatus = typeof item?.status === 'string' ? item.status.toLowerCase() : '';
  let status = ALLOWED_ITEM_STATUSES.has(rawStatus) ? rawStatus : '';

  if (!status) {
    status = 'active';
  }

  return {
    ...item,
    menuId: item?.menuId ?? menuId,
    categoryId: item?.categoryId ?? categoryId,
    status,
    isAvailable: item?.isAvailable ?? true,
  };
};

const processMenuData = (menus) => {
  const menusData = [];
  const categoriesData = [];
  const itemsData = [];

  menus.forEach(menu => {
    // Add the menu to menusData
    const { categories = [], ...menuWithoutCategories } = menu;
    menusData.push(menuWithoutCategories);

    // Process each category
    categories.forEach(category => {
      // Add the category to categoriesData
      const { items = [], ...categoryWithoutItems } = category;
      categoriesData.push({ ...categoryWithoutItems, menuId: menu.id });

      // Process each item
      items.forEach(item => {
        itemsData.push(normalizeItem(item, category.id, menu.id));
      });
    });
  });

  return { menusData, categoriesData, itemsData };
};

export const AdminProvider = ({ children }) => {
  const venueId = useVenueId();
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [floors, setFloors] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);

  const { getAccessTokenSilently } = useAuth0();

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const usersData = await fetchUsers(venueId, getAccessTokenSilently);
        const areasData = await fetchAreas(venueId, getAccessTokenSilently);
        const rolesData = await fetchRoles(getAccessTokenSilently);
        const floorsData = await fetchFloors(venueId, getAccessTokenSilently);
        const tablesData = await fetchTables(venueId, getAccessTokenSilently);
        const menus = await fetchMenus(venueId, getAccessTokenSilently);

        const { menusData, categoriesData, itemsData } = processMenuData(menus);
        setUsers(usersData);
        setAreas(areasData);
        setRoles(rolesData);
        setFloors(floorsData);
        setTables(tablesData);
        setMenus(menusData);
        setCategories(categoriesData);
        setItems(itemsData);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, venueId]);

  if (loading) {
    return <Loading />; // or any loading indicator you prefer
  }

  return (
    <AdminContext.Provider value={{
      users, setUsers,
      areas, setAreas,
      roles, setRoles,
      menus, setMenus,
      categories, setCategories,
      items, setItems,
      floors, setFloors,
      tables, setTables,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);
